<template>
  <transition name="tp-fade">
    <div v-if="visible && taskList.length > 0" class="task-progress-container">
      <div class="task-progress-panel">
        <div class="tp-close" @click="close">
          <i class="el-icon-close"></i>
        </div>
        <div class="tp-header">
          <div class="tp-header-icon">
            <i class="el-icon-s-operation"></i>
          </div>
        </div>
        <div ref="axisRef" class="tp-axis">
          <div
            v-for="(task, index) in taskList"
            :key="index"
            class="tp-item"
            :class="{ finished: task.finished }"
          >
            <div class="tp-circle-wrap">
              <div
                class="tp-circle"
                :class="{
                  active: task.finished,
                  'in-progress': !task.finished && index === firstUnfinishedIndex,
                }"
              >
                <template v-if="task.finished">✓</template>
                <i v-else-if="index === firstUnfinishedIndex" class="el-icon-loading"></i>
              </div>
            </div>
            <div
              class="tp-name"
              :class="{
                finished: task.finished,
                'in-progress': !task.finished && index === firstUnfinishedIndex,
              }"
            >
              {{ task.name || $t('workbench.taskN', { n: index + 1 }) }}
            </div>
            <div v-if="index < taskList.length - 1" class="tp-line" :class="{ active: task.finished || (!task.finished && index === firstUnfinishedIndex) }"></div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'TaskProgressPanel',
  computed: {
    ...mapState('cosmoClaw', ['taskProgressList', 'taskProgressVisible']),
    visible() {
      return this.taskProgressVisible
    },
    taskList() {
      return this.taskProgressList || []
    },
    firstUnfinishedIndex() {
      return this.taskList.findIndex(t => !t.finished)
    }
  },
  watch: {
    firstUnfinishedIndex(val, oldVal) {
      if (val !== oldVal && val >= 0) {
        this.$nextTick(() => this.scrollToStep(val))
      }
    }
  },
  methods: {
    close() {
      this.$store.commit('cosmoClaw/SET_TASK_PROGRESS_VISIBLE', false)
    },
    scrollToStep(index) {
      const axis = this.$refs.axisRef
      if (!axis) return
      const items = axis.querySelectorAll('.tp-item')
      let distance = 0
      for (let i = 0; i < index; i++) {
        distance += items[i].offsetHeight
      }
      axis.scrollTop = distance
    }
  }
}
</script>

<style lang="scss" scoped>
.task-progress-container {
  position: fixed;
  right: 20px;
  top: 70px;
  z-index: 1000;
  width: 72px;
}

.task-progress-panel {
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.tp-close {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  font-size: 14px;
  color: #24232c;
  cursor: pointer;
  z-index: 10;
  transition: color 0.2s;

  &:hover {
    color: #3370ff;
  }
}

.tp-header {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 30px;
}

.tp-header-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4a5ff3, #6c7bff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 18px;
}

.tp-axis {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: calc(100vh - 200px);
  overflow-y: auto;
  margin-top: 12px;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
}

.tp-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 40px;
  flex-shrink: 0;
  width: 100%;
  transition: all 0.3s ease;
}

.tp-circle-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 100%;
}

.tp-circle {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ddd;
  border: 4px solid #f5f4f4;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: #999;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
  margin: 15px 0;
  box-sizing: border-box;

  &.active {
    width: 24px;
    height: 24px;
    background: #3370ff;
    color: #fff;
    font-size: 12px;
    margin: 8px 0;
    border: none;
  }

  &.in-progress {
    width: 24px;
    height: 24px;
    background: #3370ff;
    color: #fff;
    font-size: 12px;
    margin: 8px 0;
    border: none;
    animation: pulse-shadow 1.5s infinite;
  }
}

@keyframes pulse-shadow {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(74, 95, 243, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(74, 95, 243, 0);
  }
}

.tp-name {
  font-size: 12px;
  color: #999;
  line-height: 1.4;
  text-align: center;
  width: 100%;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  max-height: 33.6px;
  transition: all 0.3s ease;

  &.finished {
    color: #333;
  }

  &.in-progress {
    color: #3370ff;
  }
}

.tp-line {
  width: 1px;
  height: 30px;
  position: relative;
  z-index: 1;
  align-self: center;
  background: transparent;
  border-left: 1px dashed #ddd;
  transition: all 0.3s ease;

  &.active {
    background: #3370ff;
    border-left: 1px solid #3370ff;
  }
}

.tp-fade-enter-active,
.tp-fade-leave-active {
  transition: opacity 0.3s;
}
.tp-fade-enter,
.tp-fade-leave-to {
  opacity: 0;
}
</style>
